import Component from '../../components/component/component';

import './appear.scss';

interface IAppearSettings {
    hiddenClass?: string
    threshold?: number
}

export const appearSettings: IAppearSettings = {
    hiddenClass: 'is-hidden',
    threshold: .3,
};

/**
 * The Appear component applies an appearance animation to an HTML element when it intersects with the viewport.
 * If the element would trigger the threshold, the component uses a static IntersectionObserver instance
 * to observe the element and remove a hidden class when it intersects with the viewport.
 * If the element would not trigger the threshold, the component creates a new IntersectionObserver
 * instance with a custom threshold based on the element's height and the appearSettings.threshold value.
 */
export default class Appear extends Component {
    static initSelector: string = '.appear';

    static staticObserver: IntersectionObserver;

    observer: IntersectionObserver | null;

    constructor(element: HTMLElement) {
        super(element);

        if (this.wouldTriggerThreshold()) {
            this.initDefaultObserver();
        } else {
            this.initCustomObserver();
        }

        this.element.addClass(appearSettings.hiddenClass);
    }

    static intersectionCallback(entries: IntersectionObserverEntry[]): void {
        entries.forEach((entry: IntersectionObserverEntry) => {
            if (entry.isIntersecting) {
                $(entry.target).removeClass(appearSettings.hiddenClass);
                Appear.staticObserver.unobserve(entry.target);
            }
        });
    }

    initDefaultObserver(): void {
        if (!Appear.staticObserver) {
            const observerSettings: IntersectionObserverInit = {
                threshold: appearSettings.threshold,
            };

            // eslint-disable-next-line compat/compat
            Appear.staticObserver = new IntersectionObserver(Appear.intersectionCallback, observerSettings);
        }

        Appear.staticObserver.observe(this.element.get(0));
    }

    /**
     * Creates a new IntersectionObserver instance with a custom threshold based on the window innerHeight, element's height
     * and the appearSettings.threshold value.
     *
     * Used for elements that are too big to trigger the threshold.
     */
    initCustomObserver(): void {
        const customThreshold: number = (window.innerHeight * appearSettings.threshold / this.element.height());

        const observerSettings: IntersectionObserverInit = {
            threshold: customThreshold,
        };

        // eslint-disable-next-line compat/compat
        this.observer = new IntersectionObserver(this.intersectionCallback, observerSettings);
        this.observer.observe(this.element.get(0));
    }

    intersectionCallback(entries: IntersectionObserverEntry[]): void {
        entries.forEach((entry: IntersectionObserverEntry) => {
            if (entry.isIntersecting) {
                $(entry.target).removeClass(appearSettings.hiddenClass);
                if (this.observer) {
                    this.observer.disconnect();
                }
            }
        });
    }

    /**
     * Check if element height multiplied by the threshold value is smaller than the window innerHeight.
     *
     * @returns {boolean} Whether the element would trigger the threshold.
     */
    wouldTriggerThreshold(): boolean {
        return (this.element.height() * appearSettings.threshold < window.innerHeight);
    }

    destroy(): void {
        if (this.observer) {
            this.observer.unobserve(this.element.get(0));
        } else {
            Appear.staticObserver.unobserve(this.element.get(0));
        }
    }
}
